'use client'

import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  MessageSquare,
  Clock,
  User,
  ChevronRight,
  Filter,
  Plus,
  Search,
  MoreVertical,
  Paperclip,
  Send,
  CheckCircle2,
  AlertCircle,
  Archive,
  ArrowLeft,
  Calendar,
  Tag,
  Hash,
  Globe,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Users,
  Inbox,
  Sparkles,
  Book,
  Facebook,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LoadingOverlay } from '@/components/ui/loading-overlay'
import {
  useAgentTickets,
  useAgentTicket,
  useAgentTicketMutations,
} from '@/hooks/useTickets'
import { useRouter } from '@/i18n/navigation'
import type { TicketStatus, TicketPriority } from '@/types/ticket'
import { useToast } from '@/hooks/use-toast'
import { CreateTicketDialog } from './CreateTicketDialog'
import { ArticleLibraryDialog, AssignmentPopover } from './AgentTicketTools'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import {
  AttachmentUploader,
  type Attachment,
} from '@/components/tickets/AttachmentUploader'

export function AgentTicketsPanel() {
  const router = useRouter()
  const { toast } = useToast()
  const { agentUser: user } = useAuthStore()

  // State for Tools
  const [isArticlesOpen, setIsArticlesOpen] = useState(false)
  const [isRewriting, setIsRewriting] = useState(false)

  // State for Reply Enhancements
  const [showRecipients, setShowRecipients] = useState(false)
  const [ccEmails, setCcEmails] = useState('')
  const [bccEmails, setBccEmails] = useState('')

  // State for List View
  const [scope, setScope] = useState<'assigned' | 'team' | 'all'>('assigned')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [page, setPage] = useState(1)
  const limit = 20

  // State for Detail View
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [isInternalNote, setIsInternalNote] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])

  // UI State
  const [isListExpanded, setIsListExpanded] = useState(true)
  const [isInfoExpanded, setIsInfoExpanded] = useState(false)

  // Coordination Effect: When one side expands, collapse the other to save space
  useEffect(() => {
    if (isListExpanded && isInfoExpanded) {
      setIsInfoExpanded(false)
    }
  }, [isListExpanded])

  useEffect(() => {
    if (isInfoExpanded && isListExpanded) {
      setIsListExpanded(false)
    }
  }, [isInfoExpanded])

  // Data Fetching
  const listParams = {
    scope,
    page,
    limit,
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: searchQuery || undefined,
  }
  const {
    data: listData,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useAgentTickets(listParams)

  const {
    data: ticketDetail,
    isLoading: isDetailLoading,
    refetch: refetchDetail,
  } = useAgentTicket(selectedTicketId || 0)

  const { updateTicket, createReply } = useAgentTicketMutations()

  // Derived Data
  const tickets = listData?.tickets || []
  const currentTicket = ticketDetail?.ticket
  const replies = ticketDetail?.replies || []
  const assignments = ticketDetail?.assignments || []
  const customerDisplayName =
    currentTicket?.contactName || currentTicket?.contactEmail || 'Customer'
  const customerInitials = customerDisplayName
    .trim()
    .substring(0, 2)
    .toUpperCase()

  // Check if current user can reply (ticket must be assigned to them)
  const canReply = currentTicket?.assignedToUserId === user?.id

  // Handlers
  const handleTicketSelect = (id: number) => {
    setSelectedTicketId(id)
  }

  const handleInsertText = (text: string) => {
    setReplyContent(prev => (prev ? `${prev}\n\n${text}` : text))
  }

  const handleSmartRewrite = async () => {
    if (!replyContent.trim()) {
      toast({
        title: 'Empty Content',
        description: 'Please type a message to rewrite.',
        variant: 'destructive',
      })
      return
    }

    setIsRewriting(true)
    try {
      // Using askKnowledge as a proxy for AI rewriting
      const response = await api.agent.askKnowledge({
        query: `Rewrite this customer support reply to be more professional, check grammar, and polite. Only output the rewritten text: "${replyContent}"`,
        sessionId: 'rewrite-context',
        maxResults: 1, // We just want an answer
      })

      if (response.success && response.data?.answer) {
        setReplyContent(response.data.answer)
        toast({
          title: 'AI Rewrite Success',
          description: 'Your message has been polished.',
        })
      } else {
        throw new Error(response.error || 'No suggestion generated')
      }
    } catch (error: any) {
      toast({
        title: 'Rewrite Failed',
        description: error.message || 'Could not rewrite text.',
        variant: 'destructive',
      })
    } finally {
      setIsRewriting(false)
    }
  }

  const handleReplySubmit = async () => {
    if (!selectedTicketId || !replyContent.trim()) return

    // Check if ticket is assigned to current user
    if (
      !currentTicket?.assignedToUserId ||
      currentTicket.assignedToUserId !== user?.id
    ) {
      toast({
        title: 'Cannot Reply',
        description: 'You must assign this ticket to yourself before replying.',
        variant: 'destructive',
      })
      return
    }

    try {
      const ccList = ccEmails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)
      const bccList = bccEmails
        .split(',')
        .map(e => e.trim())
        .filter(Boolean)

      await createReply.mutateAsync({
        ticketId: selectedTicketId,
        data: {
          content: replyContent,
          isInternal: isInternalNote,
          ccEmails: ccList.length ? ccList : undefined,
          bccEmails: bccList.length ? bccList : undefined,
          attachments: attachments.length > 0 ? attachments : undefined,
        },
      })

      setReplyContent('')
      // Keep recipient state if user wants to reply again? Usually clear it.
      // But maybe they want to keep the recipients. Let's clear for now.
      setCcEmails('')
      setBccEmails('')
      setAttachments([])
      setIsInternalNote(false)
      setShowRecipients(false)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send reply',
        variant: 'destructive',
      })
    }
  }

  const handleAssign = async (userId: number | null, teamId: number | null) => {
    if (!selectedTicketId) return

    if (
      userId &&
      user?.id &&
      userId === user.id &&
      currentTicket?.assignedToTeamId
    ) {
      const teamIds =
        user.teamIds || (user.primaryTeamId ? [user.primaryTeamId] : [])
      if (!teamIds.includes(currentTicket.assignedToTeamId)) {
        toast({
          title: 'Cannot Assign',
          description:
            'Only members of the assigned team can claim this ticket.',
          variant: 'destructive',
        })
        return
      }
    }

    try {
      // @ts-ignore
      await updateTicket.mutateAsync({
        id: selectedTicketId,
        data: {
          assignedToUserId: userId,
          assignedToTeamId: teamId,
        },
      })
      toast({
        title: 'Assignment Updated',
        description: userId
          ? 'Assigned to agent'
          : teamId
            ? 'Assigned to team'
            : 'Ticket unassigned',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const handleStatusUpdate = async (status: TicketStatus) => {
    if (!selectedTicketId) return
    try {
      await updateTicket.mutateAsync({
        id: selectedTicketId,
        data: { status },
      })
      toast({
        title: 'Status Updated',
        description: `Ticket marked as ${status.replace('_', ' ')}`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  const handlePriorityUpdate = async (priority: TicketPriority) => {
    if (!selectedTicketId) return
    try {
      await updateTicket.mutateAsync({
        id: selectedTicketId,
        data: { priority },
      })
      toast({
        title: 'Priority Updated',
        description: `Priority changed to ${priority}`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    }
  }

  // Helpers
  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'waiting_for_customer':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'resolved':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'closed':
        return 'bg-gray-100 text-gray-700 border-gray-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-50 border-red-100'
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-100'
      case 'medium':
        return 'text-blue-600 bg-blue-50 border-blue-100'
      case 'low':
        return 'text-gray-600 bg-gray-50 border-gray-100'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-100'
    }
  }

  const getPriorityIcon = (priority: TicketPriority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircle className="w-3 h-3 mr-1" />
      case 'high':
        return <AlertCircle className="w-3 h-3 mr-1" />
      case 'medium':
        return <CheckCircle2 className="w-3 h-3 mr-1" />
      case 'low':
        return <CheckCircle2 className="w-3 h-3 mr-1" />
      default:
        return null
    }
  }

  return (
    <div className="flex h-[calc(100vh-140px)] bg-white border rounded-lg overflow-hidden shadow-sm">
      <CreateTicketDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />
      <ArticleLibraryDialog
        open={isArticlesOpen}
        onOpenChange={setIsArticlesOpen}
        onInsert={handleInsertText}
      />

      {/* LEFT COLUMN: Ticket List */}
      <div
        className={cn(
          'border-r flex flex-col bg-gray-50/30 transition-all duration-300 ease-in-out relative',
          isListExpanded ? 'w-80' : 'w-12 bg-gray-100'
        )}
      >
        {/* Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'absolute -right-3 top-2.5 z-20 h-6 w-6 rounded-full border bg-white shadow-sm hover:bg-gray-100 p-0 hidden group-hover:flex',
            !isListExpanded && 'flex -right-3'
          )}
          onClick={() => setIsListExpanded(!isListExpanded)}
        >
          {isListExpanded ? (
            <ChevronLeft className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
        </Button>

        {/* Collapsed View State */}
        {!isListExpanded && (
          <div className="flex flex-col items-center py-4 space-y-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsListExpanded(true)}
              title="Expand List"
            >
              <PanelLeftOpen className="h-5 w-5 text-gray-500" />
            </Button>
            <Separator className="w-6" />
            <div className="flex flex-col gap-2">
              <Button
                variant={scope === 'assigned' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setScope('assigned')
                  setIsListExpanded(true)
                }}
                title="Assigned to Me"
              >
                <User className="h-4 w-4" />
              </Button>
              <Button
                variant={scope === 'team' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setScope('team')
                  setIsListExpanded(true)
                }}
                title="Team Tickets"
              >
                <Users className="h-4 w-4" />
              </Button>
              <Button
                variant={scope === 'all' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setScope('all')
                  setIsListExpanded(true)
                }}
                title="All Tickets"
              >
                <Inbox className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Expanded Content */}
        <div
          className={cn('flex flex-col h-full', !isListExpanded && 'hidden')}
        >
          <div className="p-3 border-b space-y-3 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Tickets</h2>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setIsListExpanded(false)}
                  title="Collapse List"
                >
                  <PanelLeftClose className="h-4 w-4 text-gray-500" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search details..."
                className="pl-8 bg-gray-50 border-gray-200 h-9 text-sm"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex gap-1">
              <Button
                size="sm"
                variant={scope === 'assigned' ? 'secondary' : 'ghost'}
                className={cn(
                  'flex-1 text-xs h-7',
                  scope === 'assigned' && 'bg-white shadow-sm border'
                )}
                onClick={() => setScope('assigned')}
              >
                Mine
              </Button>
              <Button
                size="sm"
                variant={scope === 'team' ? 'secondary' : 'ghost'}
                className={cn(
                  'flex-1 text-xs h-7',
                  scope === 'team' && 'bg-white shadow-sm border'
                )}
                onClick={() => setScope('team')}
              >
                Team
              </Button>
              <Button
                size="sm"
                variant={scope === 'all' ? 'secondary' : 'ghost'}
                className={cn(
                  'flex-1 text-xs h-7',
                  scope === 'all' && 'bg-white shadow-sm border'
                )}
                onClick={() => setScope('all')}
              >
                All
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isListLoading && page === 1 ? (
              <div className="p-8 flex justify-center">
                <LoadingOverlay isLoading={true} title="" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>No tickets found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {tickets.map((ticket: any) => (
                  <button
                    key={ticket.id}
                    onClick={() => handleTicketSelect(ticket.id)}
                    className={cn(
                      'w-full text-left p-3 hover:bg-white transition-colors border-l-2 border-transparent',
                      selectedTicketId === ticket.id
                        ? 'bg-white border-l-primary shadow-sm z-10'
                        : 'border-l-transparent hover:border-l-gray-300'
                    )}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-medium text-gray-500">
                        {ticket.ticketNumber}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatRelativeTime(new Date(ticket.updatedAt))}
                      </span>
                    </div>
                    <h3
                      className={cn(
                        'text-sm font-medium mb-1 line-clamp-1',
                        selectedTicketId === ticket.id
                          ? 'text-primary'
                          : 'text-gray-900'
                      )}
                    >
                      {ticket.title}
                    </h3>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                      {ticket.description || 'No description'}
                    </p>
                    <div className="flex gap-2 items-center">
                      <Badge
                        variant="outline"
                        className={cn(
                          'px-1.5 py-0 text-[10px] h-5 border-0 font-medium',
                          getStatusColor(ticket.status)
                        )}
                      >
                        {ticket.status.replace('_', ' ')}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={cn(
                          'px-1.5 py-0 text-[10px] h-5 border-0 font-medium',
                          getPriorityColor(ticket.priority)
                        )}
                      >
                        {getPriorityIcon(ticket.priority)}
                        {ticket.priority}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CENTER COLUMN: Ticket Detail */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        {selectedTicketId && currentTicket ? (
          <>
            {/* Detail Header */}
            <div className="h-14 border-b px-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                  {currentTicket.contactName?.substring(0, 2) ||
                    currentTicket.createdByUser?.username?.substring(0, 2) ||
                    'TK'}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
                    {currentTicket.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>
                      {currentTicket.contactEmail ||
                        currentTicket.createdByUser?.email ||
                        'No email'}
                    </span>
                    <span>•</span>
                    <span>
                      {format(new Date(currentTicket.createdAt), 'PPp')}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={currentTicket.status}
                  onValueChange={val => handleStatusUpdate(val as TicketStatus)}
                  disabled={!canReply}
                >
                  <SelectTrigger
                    className={cn(
                      'h-8 text-xs w-[130px] border-0',
                      getStatusColor(currentTicket.status),
                      !canReply && 'opacity-60 cursor-not-allowed'
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_for_customer">
                      Waiting
                    </SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handlePriorityUpdate('urgent')}
                    >
                      Mark as Urgent
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Conversation Feed */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
              <div className="space-y-6 max-w-3xl mx-auto">
                {/* Original Message */}
                <Card className="border-0 shadow-sm">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                          {customerInitials || 'CU'}
                        </div>
                        <div>
                          <span className="text-sm font-semibold">
                            {customerDisplayName}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            via Web
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {format(
                          new Date(currentTicket.createdAt),
                          'MMM d, h:mm a'
                        )}
                      </span>
                    </div>
                    <div className="pl-10 text-sm text-gray-800 whitespace-pre-wrap">
                      {currentTicket.description || 'No description provided.'}
                    </div>
                    {/* Attachments */}
                    {currentTicket.attachments &&
                      currentTicket.attachments.length > 0 && (
                        <div className="pl-10 mt-3 flex flex-wrap gap-2">
                          {currentTicket.attachments.map(
                            (att: any, idx: number) => (
                              <a
                                href={att.url}
                                target="_blank"
                                rel="noreferrer"
                                key={idx}
                                className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1 hover:bg-gray-50 text-blue-600"
                              >
                                <Paperclip className="h-3 w-3" />
                                {att.filename}
                              </a>
                            )
                          )}
                        </div>
                      )}
                  </div>
                </Card>

                {/* Replies */}
                {replies.map((reply: any) => (
                  <div
                    key={reply.id}
                    className={cn(
                      'flex gap-3',
                      reply.isInternal
                        ? 'bg-yellow-50/50 p-4 rounded-lg border border-yellow-100'
                        : ''
                    )}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center mt-1 text-xs font-bold',
                        reply.createdBy
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100'
                      )}
                    >
                      {reply.createdBy?.username.substring(0, 2) ||
                        customerInitials ||
                        'CU'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {reply.createdBy?.username || customerDisplayName}
                        </span>
                        {reply.isInternal && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] h-4 bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                          >
                            Internal Note
                          </Badge>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                          {format(new Date(reply.createdAt), 'h:mm a')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap">
                        {reply.content}
                      </div>
                      {reply.attachments && reply.attachments.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {reply.attachments.map((att: any, idx: number) => (
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noreferrer"
                              key={idx}
                              className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1 hover:bg-gray-50 text-blue-600"
                            >
                              <Paperclip className="h-3 w-3" />
                              {att.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reply Area */}
            <div className="p-4 bg-white border-t">
              <div className="max-w-3xl mx-auto space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <Tabs
                    value={isInternalNote ? 'note' : 'reply'}
                    onValueChange={v => setIsInternalNote(v === 'note')}
                    className="w-40"
                  >
                    <TabsList className="grid w-full grid-cols-2 h-7 bg-gray-100">
                      <TabsTrigger
                        value="reply"
                        className="text-xs h-6 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                      >
                        Reply
                      </TabsTrigger>
                      <TabsTrigger
                        value="note"
                        className="text-xs h-6 data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700"
                      >
                        Note
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  <div className="flex items-center gap-2">
                    <Button
                      variant={showRecipients ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setShowRecipients(!showRecipients)}
                      className="h-7 text-gray-500 hover:text-gray-900"
                      title="Add CC/BCC"
                    >
                      <span className="text-xs font-medium">CC/BCC</span>
                    </Button>
                    <Separator orientation="vertical" className="h-4" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSmartRewrite}
                      disabled={isRewriting || !replyContent.trim()}
                      className="h-7 border border-purple-200 bg-purple-50 text-purple-700! hover:bg-purple-100 disabled:opacity-50"
                    >
                      {isRewriting ? (
                        <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      <span className="text-xs font-medium">
                        {isRewriting ? 'Rewriting...' : 'AI Suggest'}
                      </span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsArticlesOpen(true)}
                      className="h-7 border border-blue-200 bg-blue-50 text-blue-700! hover:bg-blue-100"
                    >
                      <Book className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs font-medium">KB Search</span>
                    </Button>
                  </div>
                </div>
                <div
                  className={cn(
                    'relative rounded-md border shadow-sm transition-colors',
                    isInternalNote
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-white border-gray-200 focus-within:border-blue-500'
                  )}
                >
                  {showRecipients && !isInternalNote && (
                    <div className="flex flex-col gap-1 p-2 border-b bg-gray-50/50 rounded-t-md">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 w-8">
                          CC:
                        </span>
                        <Input
                          value={ccEmails}
                          onChange={e => setCcEmails(e.target.value)}
                          className="h-6 text-xs bg-transparent border-0 border-b border-transparent focus-visible:border-blue-200 focus-visible:ring-0 px-0 shadow-none placeholder:text-gray-400"
                          placeholder="email@example.com, ..."
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 w-8">
                          BCC:
                        </span>
                        <Input
                          value={bccEmails}
                          onChange={e => setBccEmails(e.target.value)}
                          className="h-6 text-xs bg-transparent border-0 border-b border-transparent focus-visible:border-blue-200 focus-visible:ring-0 px-0 shadow-none placeholder:text-gray-400"
                          placeholder="email@example.com, ..."
                        />
                      </div>
                    </div>
                  )}
                  <Textarea
                    value={replyContent}
                    onChange={e => setReplyContent(e.target.value)}
                    placeholder={
                      !canReply
                        ? 'Assign this ticket to yourself to reply...'
                        : isInternalNote
                          ? 'Add an internal note (visible only to agents)...'
                          : 'Type your reply to the customer...'
                    }
                    className="min-h-[100px] border-0 bg-transparent resize-none focus-visible:ring-0 p-3 text-sm"
                    disabled={!canReply}
                  />
                  <div className="flex justify-between items-start p-2 border-t border-dashed border-gray-200/50">
                    <div className="flex flex-col gap-2 max-w-[70%]">
                      <AttachmentUploader
                        ticketId={selectedTicketId || 0}
                        attachments={attachments}
                        onAttachmentsChange={setAttachments}
                        disabled={!canReply || createReply.isPending}
                        maxFiles={5}
                      />
                      <div className="text-xs flex items-center gap-1">
                        {canReply ? (
                          <>
                            <CheckCircle2 className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-400">
                              Markdown supported
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3 h-3 text-amber-500" />
                            <span className="text-amber-600 font-medium">
                              Assign to yourself first
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleReplySubmit}
                      disabled={
                        !canReply ||
                        !replyContent.trim() ||
                        createReply.isPending
                      }
                      className={cn(
                        'h-8 px-4',
                        isInternalNote
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          : ''
                      )}
                    >
                      {isInternalNote ? 'Add Note' : 'Send Reply'}
                      <Send className="w-3 h-3 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50/30">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <MessageSquare className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">
              Select a ticket
            </h3>
            <p className="text-sm max-w-xs text-center mt-2">
              Choose a ticket from the list to view details and start
              conversing.
            </p>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN: Ticket Info */}
      {selectedTicketId && currentTicket && (
        <div
          className={cn(
            'border-l bg-gray-50 flex flex-col transition-all duration-300 ease-in-out relative',
            isInfoExpanded ? 'w-72' : 'w-12 bg-white'
          )}
        >
          {/* Toggle Button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'absolute -left-3 top-2.5 z-20 h-6 w-6 rounded-full border bg-white shadow-sm hover:bg-gray-100 p-0 hidden group-hover:flex',
              !isInfoExpanded && 'flex -left-3'
            )}
            onClick={() => setIsInfoExpanded(!isInfoExpanded)}
          >
            {isInfoExpanded ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronLeft className="h-3 w-3" />
            )}
          </Button>

          {/* Collapsed View */}
          {!isInfoExpanded && (
            <div className="flex flex-col items-center py-4 space-y-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsInfoExpanded(true)}
                title="Expand Info"
              >
                <PanelRightOpen className="h-5 w-5 text-gray-400" />
              </Button>
              <Separator className="w-6" />
              <div
                className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-[10px] font-bold"
                title={currentTicket.contactName}
              >
                {currentTicket.contactName?.substring(0, 2) || 'CS'}
              </div>
            </div>
          )}

          {/* Expanded View */}
          <div
            className={cn(
              'flex flex-col h-full overflow-y-auto',
              !isInfoExpanded && 'hidden'
            )}
          >
            <div className="flex items-center justify-between p-3 border-b bg-white">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Ticket Info
              </h3>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => setIsInfoExpanded(false)}
              >
                <PanelRightClose className="h-4 w-4 text-gray-400" />
              </Button>
            </div>

            <div className="p-4 border-b bg-white">
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1.5">
                    Assignee
                  </label>
                  <div className="flex items-center gap-2 p-1.5 bg-gray-50 border rounded-md">
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center text-[10px]',
                        currentTicket.assignedToUserId
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-200 text-gray-500'
                      )}
                    >
                      {currentTicket.assignedToUserId
                        ? currentTicket.assignedToUser?.username
                            ?.substring(0, 2)
                            .toUpperCase() || 'AG'
                        : 'UN'}
                    </div>
                    <span className="text-sm text-gray-700">
                      {currentTicket.assignedToUserId
                        ? currentTicket.assignedToUserId === user?.id
                          ? 'Me'
                          : currentTicket.assignedToUser?.username || 'Agent'
                        : currentTicket.assignedToTeamId
                          ? 'Team'
                          : 'Unassigned'}
                    </span>
                    <AssignmentPopover
                      currentAssigneeId={currentTicket.assignedToUserId}
                      currentTeamId={currentTicket.assignedToTeamId}
                      onAssign={handleAssign}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-6 text-xs text-blue-600 hover:text-blue-700"
                      >
                        Change
                      </Button>
                    </AssignmentPopover>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 block mb-1.5">
                    Priority
                  </label>
                  <Select
                    value={currentTicket.priority}
                    onValueChange={val =>
                      handlePriorityUpdate(val as TicketPriority)
                    }
                    disabled={!canReply}
                  >
                    <SelectTrigger
                      className={cn(
                        'h-8 text-xs bg-white',
                        !canReply && 'opacity-60 cursor-not-allowed'
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="p-4 border-b">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Customer
              </h3>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold">
                  {currentTicket.contactName?.substring(0, 2) || 'CS'}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    {currentTicket.contactName || 'Guest User'}
                  </h4>
                  <p className="text-xs text-gray-500 break-all">
                    {currentTicket.contactEmail || 'No email'}
                  </p>
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  <span>Local Time: {format(new Date(), 'h:mm a')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Globe className="w-3.5 h-3.5 text-gray-400" />
                  <span>Language: English</span>
                </div>

                {/* Social Channels */}
                {currentTicket.contact && (
                  <div className="mt-3 pt-3 border-t">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">
                      Channels
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {currentTicket.contact.whatsappId && (
                        <a
                          href={`https://wa.me/${currentTicket.contact.whatsappId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 text-green-700 text-xs rounded border border-green-200 hover:bg-green-100 transition-colors"
                          title="Open WhatsApp"
                        >
                          <MessageSquare className="w-3 h-3" /> WhatsApp
                        </a>
                      )}
                      {currentTicket.contact.telegramId && (
                        <a
                          href={`https://t.me/${currentTicket.contact.telegramId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-sky-50 text-sky-700 text-xs rounded border border-sky-200 hover:bg-sky-100 transition-colors"
                          title="Open Telegram"
                        >
                          <Send className="w-3 h-3" /> Telegram
                        </a>
                      )}
                      {currentTicket.contact.fbMessengerId && (
                        <a
                          href={`https://m.me/${currentTicket.contact.fbMessengerId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                          title="Open Messenger"
                        >
                          <Facebook className="w-3 h-3" /> Messenger
                        </a>
                      )}
                      {!currentTicket.contact.whatsappId &&
                        !currentTicket.contact.telegramId &&
                        !currentTicket.contact.fbMessengerId && (
                          <span className="text-xs text-gray-400 italic">
                            No linked social accounts
                          </span>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                Details
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 w-24 shrink-0">ID</span>
                  <span className="text-gray-900 font-mono text-xs">
                    {currentTicket.ticketNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 w-24 shrink-0">Created</span>
                  <span className="text-gray-900 text-xs text-right whitespace-nowrap">
                    {formatRelativeTime(new Date(currentTicket.createdAt))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 w-24 shrink-0">Updated</span>
                  <span className="text-gray-900 text-xs text-right whitespace-nowrap">
                    {formatRelativeTime(new Date(currentTicket.updatedAt))}
                  </span>
                </div>
                {currentTicket.category && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 w-24 shrink-0">
                      Category
                    </span>
                    <span className="text-xs text-right bg-blue-50 text-blue-700 px-1.5 rounded">
                      {currentTicket.category.name}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500 w-24 shrink-0">Notes</span>
                  <span className="text-gray-900 text-xs text-right flex items-center gap-1 justify-end">
                    <span className="bg-yellow-50 text-yellow-700 px-1.5 rounded border border-yellow-100 font-medium">
                      {replies.filter((r: any) => r.isInternal).length}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper for formatting relative time (reuse or simpler version if lib function unavailable)
function formatRelativeTime(date: Date) {
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return format(date, 'MMM d')
}
